import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';

const ROLES = ['gerencia', 'administracion', 'produccion', 'punto_venta', 'empleado'];

const BADGE = {
  gerencia:       { bg: '#EFF6FF', color: '#1D4ED8' },
  administracion: { bg: '#F0FDF4', color: '#065F46' },
  produccion:     { bg: '#FEF3C7', color: '#92400E' },
  punto_venta:    { bg: '#FDF4FF', color: '#7E22CE' },
  empleado:       { bg: '#F3F4F6', color: '#374151' },
};

export default function Permisos() {
  const { puedeVer } = useAuth();
  const [modulos, setModulos]       = useState([]);
  const [cambios, setCambios]       = useState({});
  const [guardando, setGuardando]   = useState(null);
  const [guardadoOk, setGuardadoOk] = useState({});

  useEffect(() => {
    API.get('/modulos/permisos')
      .then(r => setModulos(r.data))
      .catch(() => toast.error('Error cargando permisos'));
  }, []);

  if (!puedeVer('gerencia')) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Solo gerencia puede ver esta sección.</div>;
  }

  const getRoles = (modulo) => {
    return cambios[modulo.path] ?? modulo.roles ?? [];
  };

  const toggleRol = (path, rol) => {
    const actual = cambios[path] ?? modulos.find(m => m.path === path)?.roles ?? [];
    const nuevo  = actual.includes(rol) ? actual.filter(r => r !== rol) : [...actual, rol];
    setCambios(c => ({ ...c, [path]: nuevo }));
    setGuardadoOk(g => ({ ...g, [path]: false }));
  };

  const guardar = async (modulo) => {
    setGuardando(modulo.path);
    try {
      const roles = getRoles(modulo);
      const pathKey = modulo.path.replace(/^\//, '');
      await API.put(`/modulos/permisos/${pathKey}`, { roles });
      // Actualizar el módulo local
      setModulos(ms => ms.map(m => m.path === modulo.path ? { ...m, roles } : m));
      setCambios(c => { const n = {...c}; delete n[modulo.path]; return n; });
      setGuardadoOk(g => ({ ...g, [modulo.path]: true }));
      toast.success(`✅ ${modulo.nombre} actualizado`);
      setTimeout(() => setGuardadoOk(g => ({ ...g, [modulo.path]: false })), 2000);
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(null); }
  };

  const tieneCambios = (path) => !!cambios[path];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>🔐 Permisos de módulos</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>
          Define qué roles tienen acceso a cada módulo del ERP. Los cambios se aplican inmediatamente.
        </p>
      </div>

      {/* Leyenda de roles */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {ROLES.map(r => (
          <span key={r} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: BADGE[r].bg, color: BADGE[r].color }}>
            {r}
          </span>
        ))}
      </div>

      {/* Tabla de módulos */}
      <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1a1a2e' }}>
              <th style={{ padding: '12px 18px', textAlign: 'left', color: 'white', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', width: '35%' }}>Módulo</th>
              {ROLES.map(r => (
                <th key={r} style={{ padding: '12px 10px', textAlign: 'center', color: 'white', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{r}</th>
              ))}
              <th style={{ padding: '12px 10px', textAlign: 'center', color: 'white', fontSize: 11, fontWeight: 700, width: 80 }}>Guardar</th>
            </tr>
          </thead>
          <tbody>
            {modulos.map((m, i) => {
              const roles = getRoles(m);
              const cambio = tieneCambios(m.path);
              const ok     = guardadoOk[m.path];
              return (
                <tr key={m.path} style={{ borderTop: '1px solid #F3F4F6', background: cambio ? '#FFFBEB' : i%2===0 ? 'white' : '#FAFAFA' }}>
                  <td style={{ padding: '12px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>{m.icon}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{m.nombre}</div>
                        <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{m.path}</div>
                      </div>
                    </div>
                  </td>
                  {ROLES.map(r => (
                    <td key={r} style={{ padding: '12px 10px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={roles.includes(r)}
                        onChange={() => toggleRol(m.path, r)}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: BADGE[r].color }}
                      />
                    </td>
                  ))}
                  <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                    {ok ? (
                      <span style={{ color: '#059669', fontSize: 18 }}>✅</span>
                    ) : (
                      <button
                        onClick={() => guardar(m)}
                        disabled={!cambio || guardando === m.path}
                        style={{
                          padding: '5px 12px', border: 'none', borderRadius: 6, cursor: cambio ? 'pointer' : 'default',
                          background: cambio ? '#2563EB' : '#E5E7EB', color: cambio ? 'white' : '#9CA3AF',
                          fontSize: 12, fontWeight: 700, transition: 'all 0.15s'
                        }}>
                        {guardando === m.path ? '⏳' : 'OK'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: '#FEF3C7', borderRadius: 10, fontSize: 13, color: '#92400E' }}>
        ⚠️ Los usuarios necesitan <strong>cerrar sesión y volver a entrar</strong> para ver los cambios aplicados en su menú.
      </div>
    </div>
  );
}
